// vim: set ts=2 sts=2 sw=2 et:
//
// This file is part of OpenLifter, simple Powerlifting meet software.
// Copyright (C) 2019 The OpenPowerlifting Project.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU Affero General Public License as
// published by the Free Software Foundation, either version 3 of the
// License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU Affero General Public License for more details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.

import { createStore, applyMiddleware } from "redux";
import { persistStore, persistReducer, createTransform } from "redux-persist";
import { composeWithDevTools } from "redux-devtools-extension";
import storage from "redux-persist/lib/storage";

import thunk from "redux-thunk";
import rootReducer from "./reducers/rootReducer";
import type { WrpfLiveState } from "./wrpf/live/platformLiveProfile";
import type { WrpfRecordsState } from "./wrpf/handoff/types";
import { recordCategoryFromJurisdiction } from "./wrpf/records/recordCategories";
import { selectEnabledRecordCategories } from "./wrpf/records/wrpfRecordsReducer";
import { reportPersistFailure } from "./wrpf/persistence/persistHealth";

import { GlobalState } from "./types/stateTypes";

// The active Platform Live profile is deliberately NOT persisted. A paired Helper profile leaves only a
// secret-free recovery candidate: the launcher must verify its exact real room before it promotes that candidate
// to active after reload. Record-marking policy and developer settings remain browser-local for their existing
// cross-window behavior, without entering exported meet files.
const wrpfLivePersistTransform = createTransform<WrpfLiveState, WrpfLiveState>(
  (state) => ({
    activeProfile: null,
    helperProfileCandidate: state.helperProfileCandidate,
    recordMarking: state.recordMarking,
    developerSettings: state.developerSettings,
  }),
  (state) => state,
  { whitelist: ["wrpfLive"] },
);

// Desde el 30/9 la web manda los ~19 mil récords y estándares de todas las jurisdicciones (~6 MB). Guardados
// enteros, el estado pasaba el límite de localStorage: redux-persist dejaba de guardar sin avisar y una recarga
// dejaba OpenLifter vacío (Ibero 2026). Se guardan solo los de las categorías habilitadas; en memoria siguen
// todos, y abrir el torneo desde la web los vuelve a traer completos.
const wrpfRecordsPersistTransform = createTransform<WrpfRecordsState, WrpfRecordsState>(
  (state) => {
    const enabled = new Set(selectEnabledRecordCategories(state));
    return {
      ...state,
      currentRecords: state.currentRecords.filter((record) => {
        const category = recordCategoryFromJurisdiction(record.jurisdiction);
        return category !== undefined && enabled.has(category);
      }),
    };
  },
  (state) => state,
  { whitelist: ["wrpfRecords"] },
);

const persistConfig = {
  key: "root",
  storage,
  transforms: [wrpfLivePersistTransform, wrpfRecordsPersistTransform],
  // Sin esto redux-persist se traga el error: el torneo deja de guardarse y nadie se entera hasta recargar.
  writeFailHandler: reportPersistFailure,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export default function configureStore(initialState: Partial<GlobalState> = {}) {
  const store = createStore(persistedReducer, initialState, composeWithDevTools(applyMiddleware(thunk)));
  const persistor = persistStore(store as any);
  return { store, persistor };
}
